import {
  MAX_USERNAME_LENGTH,
  PARTY_MAX,
  isValidUsername,
  type FriendEntry,
  type PartyMemberWire,
} from "@rietz/shared";
import { UNKNOWN_REASON } from "./hideoutLogic.js";

/**
 * The friends tab's decisions, with no React in them — same split as
 * `hideoutLogic.ts`, here for the VOCABULARY: eight reasons a button is off,
 * each a sentence written on purpose. Almost nothing can be decided client-side
 * (whether an account exists, whether they already asked), so `canInvite`
 * refuses only what it can PROVE and the server's refusal goes through the same
 * `reasonLabel`.
 */

/** The party as the store holds it — the shape `partyState` carries. */
export interface PartyView {
  members: readonly PartyMemberWire[];
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/**
 * Every social refusal code as one sentence. Its own table, not the hideout's:
 * "busy" and "full" exist in both vocabularies and mean different things. The
 * fallback (`UNKNOWN_REASON`) is shared.
 */
export function reasonLabel(reason: string): string {
  switch (reason) {
    case "unknown":
      return "No account by that name.";
    case "self":
      return "That is you.";
    case "already":
      return "That is already the case.";
    case "not-friends":
      return "You are not friends yet.";
    case "offline":
      return "They are not online.";
    case "busy":
      return "They are busy right now.";
    case "full":
      return `A squad holds ${PARTY_MAX}.`;
    case "no-invite":
      return "That invitation is gone.";
    case "not-leader":
      return "Only the squad leader can do that.";
    case "not-member":
      return "They are not in your squad.";
    case "no-party":
      return "You are not in a squad.";
    case "in-raid":
      return "Not while you are in a raid.";
    default:
      return UNKNOWN_REASON;
  }
}

// ---------------------------------------------------------------------------
// The party
// ---------------------------------------------------------------------------

/**
 * Whether we are the one who queues — by USERNAME (display names are neither
 * unique nor stable). An UNKNOWN identity (`me === null`) answers `true`: a
 * client that cannot say who it is must not be why a player cannot deploy.
 */
export function isPartyLeader(party: PartyView | null, me: string | null): boolean {
  if (party === null) return true;
  if (me === null) return true;
  return party.members.some((m) => m.username === me && m.leader);
}

/** Whether we are counted among a party's members. Same identity rule. */
export function isInParty(party: PartyView | null, me: string | null): boolean {
  if (party === null) return false;
  if (me === null) return false;
  return party.members.some((m) => m.username === me);
}

// ---------------------------------------------------------------------------
// Inviting
// ---------------------------------------------------------------------------

/** `ok: false` always carries the sentence that says why. */
export interface InviteVerdict {
  ok: boolean;
  why?: string;
}

const INVITE_OK: InviteVerdict = { ok: true };

/**
 * Whether "Invite to squad" is live for one friend, and what to say if not —
 * only the refusals the client can PROVE, most specific first. `meInParty` is a
 * separate argument because it needs the identity this file does not take.
 */
export function canInvite(
  friend: FriendEntry,
  party: PartyView | null,
  meInParty: boolean,
): InviteVerdict {
  if (party !== null && party.members.some((m) => m.username === friend.username)) {
    return { ok: false, why: "Already in your squad." };
  }
  if (!friend.online) return { ok: false, why: reasonLabel("offline") };
  if (friend.phase === "queued") return { ok: false, why: "They are queueing for a raid." };
  if (friend.phase === "raid") return { ok: false, why: "They are in a raid." };
  // No phase at all: an online friend whose server did not say what they are
  // doing (see the decoder). Not provably invitable, so it is not offered.
  if (friend.phase !== "menu") return { ok: false, why: reasonLabel("busy") };
  if (party !== null && party.members.length >= PARTY_MAX) {
    return { ok: false, why: reasonLabel("full") };
  }
  if (party !== null && !meInParty) {
    return { ok: false, why: "You are not in that squad." };
  }
  return INVITE_OK;
}

// ---------------------------------------------------------------------------
// The add-friend field
// ---------------------------------------------------------------------------

/**
 * What is wrong with what the player typed, or `null`. An empty field is `null`
 * too — not a mistake, the button just stays off. Trimmed first; the trimmed
 * string is what is sent.
 */
export function inputProblem(raw: string): string | null {
  const name = raw.trim();
  if (name.length === 0) return null;
  if (name.length > MAX_USERNAME_LENGTH) {
    return `A username is at most ${MAX_USERNAME_LENGTH} characters.`;
  }
  if (name.length < 3) return "A username is at least 3 characters.";
  if (!isValidUsername(name)) {
    return "Letters, numbers, dot, dash and underscore only.";
  }
  return null;
}

/** The string a submit would actually send: `inputProblem`'s trimmed subject. */
export function submittable(raw: string): string | null {
  const name = raw.trim();
  return name.length > 0 && inputProblem(raw) === null ? name : null;
}

// ---------------------------------------------------------------------------
// Order
// ---------------------------------------------------------------------------

/**
 * The list order: everyone online first, then by display name — a plain
 * lower-cased `<`, NOT `localeCompare`, which answers differently per browser
 * language. The username breaks a tie.
 */
export function sortFriends(friends: readonly FriendEntry[]): FriendEntry[] {
  return friends.slice().sort((a, b) => {
    if (a.online !== b.online) return a.online ? -1 : 1;
    const an = a.name.toLowerCase();
    const bn = b.name.toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    const au = a.username.toLowerCase();
    const bu = b.username.toLowerCase();
    if (au === bu) return 0;
    return au < bu ? -1 : 1;
  });
}

/** What an online friend is doing, for the row under their name. */
export function phaseLabel(friend: FriendEntry): string {
  if (!friend.online) return "Offline";
  switch (friend.phase) {
    case "menu":
      return "In menu";
    case "queued":
      return "Queued";
    case "raid":
      return "In raid";
    default:
      return "Online";
  }
}

/** The same reading for a party member, whose phase is never absent. */
export function memberPhaseLabel(member: PartyMemberWire): string {
  if (!member.online) return "Offline";
  return member.phase === "menu" ? "In menu" : member.phase === "queued" ? "Queued" : "In raid";
}
